// Excel template generator with dynamic dropdown validations using ExcelJS
import ExcelJS from 'exceljs'

export interface RecurringTemplateOption {
  id: number
  description: string
  type: 'income' | 'expense'
  amount: number
  categoryName: string
  familyMemberName: string
}

export interface TemplateGeneratorOptions {
  recurringTemplates: RecurringTemplateOption[]
  categories: string[]
  familyMembers: string[]
}

/**
 * Format a recurring template for dropdown display
 * Format: "Description - $Amount (ID:123)"
 */
function formatRecurringOption(template: RecurringTemplateOption): string {
  return `${template.description} - $${template.amount.toFixed(2)} (ID:${template.id})`
}

/**
 * Apply header styling to a row
 */
function styleHeaderRow(row: ExcelJS.Row, color: string = 'FF2E7D32') {
  row.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: color }
    }
    cell.alignment = { horizontal: 'center', wrapText: true }
  })
}

/**
 * Apply helper row styling (light gray italic)
 */
function styleHelperRow(row: ExcelJS.Row) {
  row.eachCell((cell) => {
    cell.font = { italic: true, color: { argb: 'FF666666' }, size: 9 }
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF5F5F5' }
    }
  })
}

/**
 * Generate a comprehensive Excel template with:
 * - Transactions sheet (for individual income/expense entries)
 * - Recurring Income sheet (for recurring income templates)
 * - Recurring Expenses sheet (for recurring expense templates)
 * - Reference sheet (hidden, contains dropdown values)
 */
export async function generateBudgetExcelTemplate(options: TemplateGeneratorOptions): Promise<ArrayBuffer> {
  const workbook = new ExcelJS.Workbook()
  workbook.creator = 'FamilyBudget'
  workbook.created = new Date()

  // Build dropdown options
  const typeOptions = ['income', 'expense']
  const frequencyOptions = ['weekly', 'bi-weekly', 'semi-monthly', 'monthly', 'quarterly', 'annual']
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December']
  const yesNoOptions = ['Y', 'N']
  const recurringOptions = [
    'None (one-time)',
    ...options.recurringTemplates.map(formatRecurringOption)
  ]

  // ===== Reference Sheet (contains dropdown values and examples) =====
  const refSheet = workbook.addWorksheet('Reference')

  // Add title
  const titleRow = refSheet.addRow(['REFERENCE & EXAMPLES'])
  titleRow.getCell(1).font = { bold: true, size: 14 }
  refSheet.mergeCells('A1:H1')

  // Add description
  refSheet.addRow(['This sheet contains dropdown options and example data. Do not modify the dropdown values below.'])
  refSheet.addRow([]) // Empty row

  // === EXAMPLES SECTION ===
  const examplesHeaderRow = refSheet.addRow(['EXAMPLES'])
  examplesHeaderRow.getCell(1).font = { bold: true, size: 12, color: { argb: 'FF1565C0' } }

  // Transaction example
  refSheet.addRow(['Transaction Example:', 'expense', '2024-01-15', '125.50', 'Food', 'Weekly groceries', 'Shared', 'None (one-time)'])
  refSheet.addRow(['Income Example:', 'income', '2024-01-15', '3500.00', 'Salary', 'Bi-weekly paycheck', 'Alex', 'None (one-time)'])

  // Recurring example
  refSheet.addRow(['Recurring Example:', 'Monthly Rent', '2100', 'Housing', 'Shared', 'monthly', 'Day: 1', 'Active: Y'])
  refSheet.addRow(['Bi-weekly Example:', 'Paycheck', '3500', 'Salary', 'Alex', 'bi-weekly', 'Anchor: 2024-01-05', 'Active: Y'])

  refSheet.addRow([]) // Empty row

  // === DROPDOWN VALUES SECTION ===
  const dropdownHeaderRow = refSheet.addRow(['DROPDOWN VALUES (do not edit below this line)'])
  dropdownHeaderRow.getCell(1).font = { bold: true, size: 12, color: { argb: 'FFC62828' } }

  // Add headers for dropdown values
  const refHeaders = ['Types', 'Categories', 'Family Members', 'Recurring', 'Frequencies', 'Days of Week', 'Months', 'YesNo']
  const headerRow = refSheet.addRow(refHeaders)
  styleHeaderRow(headerRow, 'FF424242')

  // Track the starting row for dropdown values (after headers)
  const dropdownStartRow = refSheet.rowCount + 1

  // Find max rows needed
  const maxRows = Math.max(
    typeOptions.length,
    options.categories.length,
    options.familyMembers.length,
    recurringOptions.length,
    frequencyOptions.length,
    daysOfWeek.length,
    months.length
  )

  for (let i = 0; i < maxRows; i++) {
    refSheet.addRow([
      typeOptions[i] || '',
      options.categories[i] || '',
      options.familyMembers[i] || '',
      recurringOptions[i] || '',
      frequencyOptions[i] || '',
      daysOfWeek[i] || '',
      months[i] || '',
      yesNoOptions[i] || ''
    ])
  }

  // Set column widths
  refSheet.columns = [
    { width: 18 }, { width: 25 }, { width: 20 }, { width: 50 },
    { width: 15 }, { width: 14 }, { width: 12 }, { width: 8 }
  ]

  // Update formula references to use the correct row numbers
  const dropdownEndRow = refSheet.rowCount

  // Define formula references using dynamic row numbers
  const typeFormula = `Reference!$A$${dropdownStartRow}:$A$${dropdownStartRow + typeOptions.length - 1}`
  const categoryFormula = options.categories.length > 0
    ? `Reference!$B$${dropdownStartRow}:$B$${dropdownStartRow + options.categories.length - 1}`
    : undefined
  const familyMemberFormula = options.familyMembers.length > 0
    ? `Reference!$C$${dropdownStartRow}:$C$${dropdownStartRow + options.familyMembers.length - 1}`
    : undefined
  const recurringFormula = `Reference!$D$${dropdownStartRow}:$D$${dropdownStartRow + recurringOptions.length - 1}`
  const frequencyFormula = `Reference!$E$${dropdownStartRow}:$E$${dropdownStartRow + frequencyOptions.length - 1}`
  const dayOfWeekFormula = `Reference!$F$${dropdownStartRow}:$F$${dropdownStartRow + daysOfWeek.length - 1}`
  const monthFormula = `Reference!$G$${dropdownStartRow}:$G$${dropdownStartRow + months.length - 1}`
  const yesNoFormula = `Reference!$H$${dropdownStartRow}:$H$${dropdownStartRow + yesNoOptions.length - 1}`

  // ===== Transactions Sheet =====
  const transSheet = workbook.addWorksheet('Transactions')

  const transHeaders = ['Type', 'Date', 'Amount', 'Category', 'Description', 'Family Member', 'Recurring Expense']
  const transHeaderRow = transSheet.addRow(transHeaders)
  styleHeaderRow(transHeaderRow)

  transSheet.columns = [
    { width: 12 }, { width: 14 }, { width: 14 }, { width: 18 },
    { width: 35 }, { width: 18 }, { width: 45 }
  ]

  // Apply data validations to transaction rows (no sample data - see Reference sheet for examples)
  for (let row = 2; row <= 1000; row++) {
    transSheet.getCell(`A${row}`).dataValidation = {
      type: 'list', allowBlank: true, formulae: [typeFormula],
      showErrorMessage: true, errorStyle: 'error',
      errorTitle: 'Invalid Type', error: 'Select income or expense'
    }
    if (categoryFormula) {
      transSheet.getCell(`D${row}`).dataValidation = {
        type: 'list', allowBlank: true, formulae: [categoryFormula],
        showErrorMessage: true, errorStyle: 'warning',
        errorTitle: 'Unknown Category', error: 'Consider using a category from the list'
      }
    }
    if (familyMemberFormula) {
      transSheet.getCell(`F${row}`).dataValidation = {
        type: 'list', allowBlank: true, formulae: [familyMemberFormula],
        showErrorMessage: true, errorStyle: 'warning',
        errorTitle: 'Unknown Family Member', error: 'Consider using a family member from the list'
      }
    }
    transSheet.getCell(`G${row}`).dataValidation = {
      type: 'list', allowBlank: true, formulae: [recurringFormula],
      showErrorMessage: true, errorStyle: 'warning',
      errorTitle: 'Unknown Recurring', error: 'Select from existing recurring templates or None'
    }
  }

  transSheet.views = [{ state: 'frozen', ySplit: 1 }]

  // ===== Recurring Income Sheet =====
  const incomeSheet = workbook.addWorksheet('Recurring Income')
  createRecurringSheet(incomeSheet, 'income', {
    categoryFormula, familyMemberFormula, frequencyFormula,
    dayOfWeekFormula, monthFormula, yesNoFormula
  })

  // ===== Recurring Expenses Sheet =====
  const expenseSheet = workbook.addWorksheet('Recurring Expenses')
  createRecurringSheet(expenseSheet, 'expense', {
    categoryFormula, familyMemberFormula, frequencyFormula,
    dayOfWeekFormula, monthFormula, yesNoFormula
  })

  // Write to buffer
  const buffer = await workbook.xlsx.writeBuffer()
  return buffer as ArrayBuffer
}

/**
 * Create a recurring template sheet (income or expense)
 */
function createRecurringSheet(
  sheet: ExcelJS.Worksheet,
  type: 'income' | 'expense',
  formulas: {
    categoryFormula?: string
    familyMemberFormula?: string
    frequencyFormula: string
    dayOfWeekFormula: string
    monthFormula: string
    yesNoFormula: string
  }
) {
  const headers = [
    'Unique Name', 'Amount', 'Category', 'Family Member', 'Frequency',
    'Day of Month', 'Second Day', 'Day of Week', 'Anchor Date',
    'Quarter Start Month', 'Annual Month', 'Is Active', 'Description'
  ]

  const helperText = [
    'Required unique ID', 'Required', 'Required', 'Required', 'Required',
    '1-31 (monthly/quarterly/annual/semi-monthly)', '1-31 (semi-monthly only)',
    'For weekly', 'YYYY-MM-DD (bi-weekly)', 'For quarterly', 'For annual', 'Y/N', 'Optional notes'
  ]

  // Add header row
  const headerRow = sheet.addRow(headers)
  styleHeaderRow(headerRow, type === 'income' ? 'FF1565C0' : 'FFC62828')

  // Add helper row
  const helperRow = sheet.addRow(helperText)
  styleHelperRow(helperRow)

  // Set column widths
  sheet.columns = [
    { width: 25 }, { width: 12 }, { width: 18 }, { width: 18 }, { width: 14 },
    { width: 14 }, { width: 12 }, { width: 14 }, { width: 14 },
    { width: 18 }, { width: 14 }, { width: 10 }, { width: 30 }
  ]

  // No sample rows - see Reference sheet for examples
  // Apply data validations (starting from row 3, after header and helper)
  for (let row = 3; row <= 500; row++) {
    // Category dropdown
    if (formulas.categoryFormula) {
      sheet.getCell(`C${row}`).dataValidation = {
        type: 'list', allowBlank: true, formulae: [formulas.categoryFormula],
        showErrorMessage: true, errorStyle: 'warning'
      }
    }
    // Family Member dropdown
    if (formulas.familyMemberFormula) {
      sheet.getCell(`D${row}`).dataValidation = {
        type: 'list', allowBlank: true, formulae: [formulas.familyMemberFormula],
        showErrorMessage: true, errorStyle: 'warning'
      }
    }
    // Frequency dropdown
    sheet.getCell(`E${row}`).dataValidation = {
      type: 'list', allowBlank: true, formulae: [formulas.frequencyFormula],
      showErrorMessage: true, errorStyle: 'error',
      errorTitle: 'Invalid Frequency', error: 'Select a valid frequency'
    }
    // Day of Week dropdown
    sheet.getCell(`H${row}`).dataValidation = {
      type: 'list', allowBlank: true, formulae: [formulas.dayOfWeekFormula]
    }
    // Quarter Start Month dropdown
    sheet.getCell(`J${row}`).dataValidation = {
      type: 'list', allowBlank: true, formulae: [formulas.monthFormula]
    }
    // Annual Month dropdown
    sheet.getCell(`K${row}`).dataValidation = {
      type: 'list', allowBlank: true, formulae: [formulas.monthFormula]
    }
    // Is Active dropdown
    sheet.getCell(`L${row}`).dataValidation = {
      type: 'list', allowBlank: true, formulae: [formulas.yesNoFormula]
    }
  }

  sheet.views = [{ state: 'frozen', ySplit: 2 }]
}

/**
 * Download the generated Excel template
 */
export async function downloadExcelTemplate(options: TemplateGeneratorOptions): Promise<void> {
  const buffer = await generateBudgetExcelTemplate(options)
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })

  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'FamilyBudget-Import-Template.xlsx'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
